<script lang="ts" setup>
import {
  FloatingBox,
  guest,
  SlideTransition,
  useClickOutside,
  useDisclosure,
  userInjectionKey,
  Fragment,
} from '#shared'
import { computed, inject, unref, capitalize, useCssModule, useTemplateRef } from 'vue'

const { href, kind = 'destyled' } = defineProps<{ href: string; kind?: 'emphasized' | 'destyled' }>()

const user = inject(userInjectionKey)
const canFollowLink = computed(() => !!unref(user) && unref(user) !== guest)

const $style = useCssModule()

const classes = computed(() => [$style.root, $style[`rootKind${capitalize(kind)}`], $style.rootDisabled])

const linkPopupDisclosure = useDisclosure()
const disabledLinkEl = useTemplateRef('disabledLinkEl')
useClickOutside(
  computed(() => [disabledLinkEl.value]),
  () => linkPopupDisclosure.close()
)
</script>

<template>
  <a v-if="canFollowLink" :class="classes" :href="href" target="_blank" data-link>
    <slot v-if="$slots.default" />
    <template v-else>{{ href }}</template>
  </a>
  <Fragment v-else>
    <span :class="classes" data-link ref="disabledLinkEl" @click="linkPopupDisclosure.open">
      <slot v-if="$slots.default" />
      <template v-else>{{ href }}</template>
    </span>

    <SlideTransition :offsetY="10">
      <FloatingBox
        v-if="linkPopupDisclosure.isOpen"
        :targetEl="disabledLinkEl"
        horizontalPosition="center"
        verticalPosition="top-outside"
        :verticalOffset="-5"
      >
        <div :class="$style.disabledLinkPopup">You have to be logged in to follow this link</div>
      </FloatingBox>
    </SlideTransition>
  </Fragment>
</template>

<style lang="scss" module>
.rootKindEmphasized {
  color: #4a8dff !important;
}

.rootKindDestyled {
  color: inherit !important;
}

.rootDisabled {
  cursor: default;
}

.disabledLinkPopup {
  background-color: #1f1f22;
  color: white;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  border: 1px solid #404045;
  white-space: nowrap;
  width: fit-content;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
}
</style>
